<!-- 顶部导航栏组件 - 虎牙风格 -->
<template>
  <header class="header">
    <div class="container header-inner">
      <div class="logo">
        <router-link to="/" class="logo-link">
          <img src="../assets/logo.png" alt="灵眸云图" class="logo-image">
        </router-link>
      </div>

      <!-- 主导航 -->
      <nav class="main-nav">
        <router-link to="/" class="main-nav-item" :class="{ active: $route.path === '/' }">首页</router-link>
        <div class="main-nav-item category-nav" :class="{ active: showCategoryPanel }" @mouseenter="openCategoryPanel" @mouseleave="closeCategoryPanel">
          <span>分类</span>
          <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"/></svg>
          <transition name="category-panel-fade">
            <div v-if="showCategoryPanel" class="category-panel" @mouseenter="keepCategoryPanel" @mouseleave="closeCategoryPanel(0)">
              <div class="category-panel-arrow"></div>
              <div v-if="categoryLoading" class="category-panel-loading">加载中...</div>
              <div v-else class="category-panel-grid">
                <div v-for="cat in categoryList" :key="cat.id" class="category-panel-item" @click="goRoomByCategory(cat.id)">
                  <img v-if="cat.icon || cat.cover" :src="getFullImageUrl(cat.icon || cat.cover)" alt="" @error="handleImgError">
                  <span>{{ cat.name }}</span>
                </div>
                <div v-if="!categoryList.length" class="category-panel-empty">暂无分类</div>
              </div>
            </div>
          </transition>
        </div>
      </nav>

      <div class="search-box">
        <select v-model="searchType" class="search-type" @change="handleSearch">
          <option value="all">综合</option>
          <option value="anchor">主播</option>
          <option value="room">直播间</option>
          <option value="video">视频</option>
          <option value="user">用户</option>
          <option value="post">动态</option>
        </select>
        <input
          type="text"
          v-model="searchKeyword"
          @keyup.enter="handleSearch"
          @focus="handleSearchFocus"
          @blur="handleSearchBlur"
          placeholder="搜索主播、游戏、内容"
        >
        <button class="search-btn" @click="handleSearch" aria-label="搜索">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
        </button>

        <!-- 热门搜索词浮层 -->
        <div class="search-results hot-keywords-panel" v-if="searchInputFocused && !searchKeyword.trim() && hotKeywords.length > 0 && !showSearchResults">
          <div class="hot-title">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#ff6a00" stroke-width="2">
              <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
            </svg>
            热门搜索
          </div>
          <div class="hot-list">
            <span
              v-for="(kw, idx) in hotKeywords"
              :key="idx"
              class="hot-item"
              @mousedown.prevent="searchHotKeyword(typeof kw === 'string' ? kw : kw.keyword)"
            >
              <span class="hot-rank" :class="'rank-' + (idx + 1)">{{ idx + 1 }}</span>
              <span class="hot-text">{{ typeof kw === 'string' ? kw : kw.keyword }}</span>
            </span>
          </div>
        </div>

        <!-- 综合搜索结果浮层 -->
        <div class="search-results search-all-results" v-if="showSearchResults">
          <div v-if="!hasAnyResult" class="no-result-tip">暂无搜索结果</div>
          <template v-else>
            <!-- 主播 -->
            <div v-if="searchAllResults.anchor && searchAllResults.anchor.length > 0" class="result-group">
              <div class="result-group-title">主播</div>
              <div
                v-for="item in searchAllResults.anchor"
                :key="'anchor-' + item.id"
                class="search-result-item anchor-result-item"
                @click="goAnchor(item.id)"
              >
                <img :src="getFullImageUrl(item.avatar)" :alt="item.nickname" class="result-avatar" @error="handleImgError">
                <div class="result-info">
                  <div class="result-title">{{ item.nickname }}</div>
                  <div class="result-meta">
                    <span class="result-author">{{ item.bio || '人气主播' }}</span>
                    <span class="result-views" v-if="item.followers">{{ formatViews(item.followers) }}粉丝</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 直播间 -->
            <div v-if="searchAllResults.room && searchAllResults.room.length > 0" class="result-group">
              <div class="result-group-title">直播间</div>
              <div
                v-for="item in searchAllResults.room"
                :key="'room-' + item.id"
                class="search-result-item"
                @click="goRoom(item.id)"
              >
                <img :src="getFullImageUrl(item.cover)" :alt="item.title" class="result-thumb" @error="handleImgError">
                <div class="result-info">
                  <div class="result-title">{{ item.title }}</div>
                  <div class="result-meta">
                    <span class="result-author">{{ item.anchor_name || item.user?.nickname }}</span>
                    <span class="result-views" v-if="item.online">{{ formatViews(item.online) }}人在线</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 视频 -->
            <div v-if="searchAllResults.video && searchAllResults.video.length > 0" class="result-group">
              <div class="result-group-title">视频</div>
              <div
                v-for="item in searchAllResults.video"
                :key="'video-' + item.id"
                class="search-result-item"
                @click="goToVideo(item.id)"
              >
                <img :src="getFullImageUrl(item.cover)" :alt="item.title" class="result-thumb" @error="handleImgError">
                <div class="result-info">
                  <div class="result-title">{{ item.title }}</div>
                  <div class="result-meta">
                    <span class="result-author">{{ item.user?.nickname }}</span>
                    <span class="result-views">{{ formatViews(item.views) }}观看</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 用户 -->
            <div v-if="searchAllResults.user && searchAllResults.user.length > 0" class="result-group">
              <div class="result-group-title">用户</div>
              <div
                v-for="item in searchAllResults.user"
                :key="'user-' + item.id"
                class="search-result-item anchor-result-item"
                @click="goUser(item.id)"
              >
                <img :src="getFullImageUrl(item.avatar)" :alt="item.nickname" class="result-avatar" @error="handleImgError">
                <div class="result-info">
                  <div class="result-title">{{ item.nickname }}</div>
                  <div class="result-meta">
                    <span class="result-author">{{ item.bio || '灵眸用户' }}</span>
                    <span class="result-views" v-if="item.score">{{ formatViews(item.score) }}积分</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 动态 -->
            <div v-if="searchAllResults.post && searchAllResults.post.length > 0" class="result-group">
              <div class="result-group-title">动态</div>
              <div
                v-for="item in searchAllResults.post"
                :key="'post-' + item.id"
                class="search-result-item"
                @click="goPost(item)"
              >
                <div class="result-info post-result-info">
                  <div class="result-title post-result-title">{{ item.content }}</div>
                  <div class="result-meta">
                    <span class="result-author">{{ item.nickname || item.username }}</span>
                    <span class="result-views" v-if="item.like_count">{{ formatViews(item.like_count) }}赞</span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- 视频搜索结果下拉框（兼容回退） -->
        <div class="search-results" v-if="showResults && searchResults.length > 0">
          <div
            v-for="item in searchResults"
            :key="item.id"
            class="search-result-item"
            @click="goToVideo(item.id)"
          >
            <img :src="getFullImageUrl(item.cover)" :alt="item.title" class="result-thumb" @error="handleImgError">
            <div class="result-info">
              <div class="result-title">{{ item.title }}</div>
              <div class="result-meta">
                <span class="result-author">{{ item.user?.nickname }}</span>
                <span class="result-views">{{ formatViews(item.views) }}观看</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 虎牙式图标导航 -->
      <nav class="icon-nav">
        <div
          v-for="item in iconNavItems"
          :key="item.key"
          class="icon-nav-item"
          :class="{ 'has-panel': hasPanel(item.key), active: activeNavPanel === item.key }"
          @click="handleIconNav(item)"
          @mouseenter="hasPanel(item.key) && openNavPanel(item.key)"
          @mouseleave="hasPanel(item.key) && closeNavPanel()"
        >
          <span class="icon-nav-svg" v-html="item.icon"></span>
          <span class="icon-nav-text">{{ item.text }}</span>
        </div>

        <!-- 悬浮下拉面板 -->
        <transition name="nav-panel-fade">
          <div
            v-if="activeNavPanel"
            class="nav-panel"
            @mouseenter="keepNavPanel"
            @mouseleave="closeNavPanel(0)"
          >
            <div class="nav-panel-arrow"></div>
            <div class="nav-panel-body">
              <!-- 关注面板 -->
              <template v-if="activeNavPanel === 'follow'">
                <div v-if="!userStore.userInfo" class="nav-panel-empty">请先登录</div>
                <div v-else-if="navPanelLoading" class="nav-panel-loading">加载中...</div>
                <div v-else-if="!followList.length" class="nav-panel-empty">
                  <p>暂无关注</p>
                  <span>你可以在主播直播间进行关注喔！</span>
                </div>
                <div v-else class="nav-panel-list">
                  <div v-for="item in followList" :key="item.id" class="nav-panel-item" @click="goRoom(item.live_user_id || item.id)">
                    <img :src="getFullImageUrl(item.avatar)" alt="" @error="handleAvatarError">
                    <div class="nav-panel-info">
                      <span class="nav-panel-name">{{ item.nickname }}</span>
                      <span class="nav-panel-tag" v-if="item.is_live">直播中</span>
                    </div>
                  </div>
                  <router-link class="nav-panel-more" :to="{ path: '/user', query: { tab: 'follow_history' } }">更多 ></router-link>
                </div>
              </template>

              <!-- 历史面板 -->
              <template v-if="activeNavPanel === 'history'">
                <div v-if="!userStore.userInfo" class="nav-panel-empty">请先登录</div>
                <div v-else-if="navPanelLoading" class="nav-panel-loading">加载中...</div>
                <div v-else-if="!historyList.length" class="nav-panel-empty">
                  <p>暂无观看记录</p>
                </div>
                <div v-else class="nav-panel-list">
                  <div v-for="item in historyList" :key="item.id" class="nav-panel-item" @click="goToVideo(item.video_id || item.id)">
                    <img :src="getFullImageUrl(item.cover || item.video_cover)" alt="" @error="handleImgError">
                    <div class="nav-panel-info">
                      <span class="nav-panel-name">{{ item.title || item.video_title }}</span>
                      <span class="nav-panel-meta">{{ item.anchor_name || item.category_name }}</span>
                    </div>
                  </div>
                  <router-link class="nav-panel-more" :to="{ path: '/user', query: { tab: 'history' } }">更多 ></router-link>
                </div>
              </template>

              <!-- 任务面板 -->
              <template v-if="activeNavPanel === 'task'">
                <div v-if="!userStore.userInfo" class="nav-panel-empty">请先登录</div>
                <div v-else-if="navPanelLoading" class="nav-panel-loading">加载中...</div>
                <div v-else-if="!taskList.length" class="nav-panel-empty">
                  <p>暂无任务</p>
                </div>
                <div v-else class="nav-panel-list">
                  <div v-for="item in taskList" :key="item.id" class="nav-panel-item task-panel-item">
                    <div class="nav-panel-info">
                      <span class="nav-panel-name">
                        {{ item.name }}
                        <span class="task-type-tag" :class="item.type">{{ item.type === 'daily' ? '每日' : '新手' }}</span>
                      </span>
                      <span class="nav-panel-meta">{{ item.description }}</span>
                      <div class="task-progress-bar">
                        <div class="task-progress-fill" :style="{ width: taskProgressPercent(item) + '%' }"></div>
                      </div>
                      <span class="task-progress-text">{{ item.done_count }}/{{ item.target_count }}</span>
                    </div>
                    <span class="nav-panel-reward" v-if="item.reward_amount">
                      +{{ item.reward_amount }}{{ item.reward_type === 'money' ? '金币' : '积分' }}
                    </span>
                    <button v-if="item.is_claimed" class="task-status-tag claimed" disabled>已领取</button>
                    <button v-else-if="item.is_completed && item.task_key === 'daily_sign'" class="task-status-tag claimable" @click="handleTaskClaim(item)">领取</button>
                    <button v-else-if="item.is_completed" class="task-status-tag claimable" @click="handleTaskClaim(item)">领取</button>
                    <span v-else class="task-status-tag progress">进行中</span>
                  </div>
                  <router-link class="nav-panel-more" :to="{ path: '/user', query: { tab: 'task' } }">更多 ></router-link>
                </div>
              </template>
            </div>
          </div>
        </transition>
      </nav>

      <!-- 用户区域 -->
      <div class="user-area" v-if="!userStore.userInfo">
        <router-link to="/login" class="login-btn">登录</router-link>
        <router-link to="/register" class="register-btn">注册</router-link>
      </div>
      <div class="user-area" v-else ref="dropdownTrigger" @mouseenter="openUserDropdown" @mouseleave="showDropdown = false">
        <div
          class="avatar-wrap"
          role="button"
          tabindex="0"
          aria-label="打开用户菜单"
          @click.stop="showDropdown = true"
          @keydown.enter.stop="showDropdown = true"
        >
          <img
            :src="getFullImageUrl(userStore.userInfo?.avatar)"
            class="avatar"
            :alt="userStore.userInfo?.nickname"
            @error="handleAvatarError"
          >
        </div>

        <!-- 用户卡片下拉 -->
        <transition name="dropdown-fade">
          <div class="user-card-dropdown" v-show="showDropdown">
            <div class="ucd-arrow"></div>
            <div class="ucd-header">
              <a class="ucd-logout" href="javascript:void(0)" @click="handleLogout">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                退出
              </a>
            </div>

            <div class="ucd-user">
              <img
                class="ucd-avatar"
                :src="getFullImageUrl(userStore.userInfo?.avatar)"
                :alt="userStore.userInfo?.nickname"
                @error="handleAvatarError"
              >
              <div class="ucd-name-row">
                <span class="ucd-nickname">{{ userStore.userInfo?.nickname || '未设置昵称' }}</span>
                <span class="ucd-gender" v-html="genderIcon"></span>
                <router-link class="ucd-edit" :to="{ path: '/user', query: { tab: 'basic' } }" @click="showDropdown = false">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </router-link>
              </div>
              <div class="ucd-level">
                <span class="ucd-level-badge">
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                  Lv {{ liveLevel }}
                </span>
                <router-link class="ucd-level-tag" :to="{ path: '/user', query: { tab: 'level' } }" @click="showDropdown = false">
                  加速升级
                  <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
                </router-link>
              </div>
              <div class="ucd-progress-wrap">
                <span class="ucd-level-label">LV-{{ liveLevel }}</span>
                <div class="ucd-progress-bar">
                  <div class="ucd-progress-fill" :style="{ width: levelPercent + '%' }"></div>
                </div>
                <span class="ucd-level-label">LV-{{ liveLevel + 1 }}</span>
              </div>
              <div class="ucd-progress-text">{{ liveExp }}/{{ liveNeedExp }}</div>
            </div>

            <div class="ucd-assets">
              <div class="ucd-assets-left">
                <span class="ucd-assets-label">资产</span>
                <span class="ucd-assets-item">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#ff9600" stroke-width="2">
                    <rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/>
                  </svg>
                  {{ userStore.userInfo?.money || '0.00' }}
                </span>
                <span class="ucd-assets-item">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#3b82f6" stroke-width="2">
                    <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                  </svg>
                  {{ userStore.userInfo?.score || 0 }}
                </span>
              </div>
              <router-link class="ucd-recharge" :to="{ path: '/user', query: { tab: 'recharge' } }" @click="showDropdown = false">充值</router-link>
            </div>

            <div class="ucd-quick">
              <router-link
                v-for="q in quickEntries"
                :key="q.key"
                class="ucd-quick-item"
                :to="{ path: '/user', query: { tab: q.tab } }"
                @click="showDropdown = false"
              >
                <span class="ucd-quick-icon" v-html="q.icon"></span>
                <span class="ucd-quick-text">{{ q.text }}</span>
              </router-link>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </header>
</template>

<script>
/* eslint-disable no-unused-vars */
import { useRouter } from 'vue-router';
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useUserStore } from '../stores/user';
import { BASE_URL } from '../api/config';
import { searchVideo } from '../api/video';
import { search as apiSearch, hotSearch } from '../api/search';
import { getFollowList } from '../api/follow';
import { getWatchHistoryList } from '../api/watchHistory';
import { getTaskList, claimTaskReward, dailySignin } from '../api/task';
import { getUserInfo } from '../api/user';
import { getRealnameStatus } from '../api/realname';
import { toast } from './Toast.vue';
import { getCategoryList } from '../api/video';

export default {
  name: 'PageHeader',
  setup() {
    const router = useRouter();
    const userStore = useUserStore();
    const showDropdown = ref(false);
    const dropdownTrigger = ref(null);
    const searchKeyword = ref('');
    const searchResults = ref([]);
    const showResults = ref(false);
    const defaultAvatarLogo = require('../assets/logo.png');

    // 综合搜索相关
    const searchType = ref('all');
    const searchAllResults = ref({ anchor: [], room: [], video: [], user: [], post: [] });
    const showSearchResults = ref(false);
    const hotKeywords = ref([]);
    const searchInputFocused = ref(false);

    // 顶部导航下拉面板
    const activeNavPanel = ref(null);
    const navPanelLoading = ref(false);
    const followList = ref([]);
    const historyList = ref([]);
    const taskList = ref([]);
    let navPanelTimer = null;

    // 分类面板
    const showCategoryPanel = ref(false);
    const categoryLoading = ref(false);
    const categoryList = ref([]);
    let categoryPanelTimer = null;

    userStore.initUserInfo();

    const liveLevel = computed(() => {
      return Math.max(1, Number(userStore.userInfo?.live_level) || 1);
    });

    const liveExp = computed(() => {
      return Math.max(0, Number(userStore.userInfo?.live_exp) || 0);
    });

    const liveNeedExp = computed(() => {
      return Math.max(1, liveLevel.value * 200);
    });

    const levelPercent = computed(() => {
      return Math.min((liveExp.value / liveNeedExp.value) * 100, 100);
    });

    const genderIcon = computed(() => {
      if (userStore.userInfo?.gender === 1) {
        return '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#3b82f6" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M8 10h8"/></svg>';
      }
      return '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#ec4899" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M8 12h8"/></svg>';
    });

    // 青少年模式：顶部导航同样隐藏开播/关注/历史/任务，
    // 与个人中心保持一致，否则这里点进去会被拦回首页，体验割裂
    const minorMode = ref(false);
    const fetchMinorMode = () => {
      getRealnameStatus().then(res => {
        const d = res && (res.data || res);
        minorMode.value = !!(d && Number(d.minor_mode) === 1);
      }).catch(() => { /* 异常时保持显示，不影响正常用户 */ });
    };
    const onMinorModeChanged = () => fetchMinorMode();

    const allIconNavItems = [
      {
        key: 'broadcast',
        text: '开播',
        route: '/broadcast',
        icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="18" height="12" rx="3"/><circle cx="11" cy="12" r="3" fill="currentColor"/><path d="M22 9v6"/></svg>'
      },
      {
        key: 'follow',
        text: '关注',
        route: { path: '/user', query: { tab: 'follow_history' } },
        icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>'
      },
      {
        key: 'history',
        text: '历史',
        route: { path: '/user', query: { tab: 'history' } },
        icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>'
      },
      {
        key: 'task',
        text: '任务',
        route: { path: '/user', query: { tab: 'task' } },
        icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M12 8l1.5 3.5H17l-3 2.5 1 3.5-3-2.5-3 2.5 1-3.5-3-2.5h3.5L12 8z"/></svg>'
      }
    ];
    // 青少年模式下四项全部隐藏
    const iconNavItems = computed(() => minorMode.value ? [] : allIconNavItems);

    const allQuickEntries = [
      {
        key: 'profile',
        text: '个人中心',
        tab: 'basic',
        icon: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>'
      },
      {
        key: 'noble',
        text: '我的贵族',
        tab: 'noble',
        icon: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M12 2l2.4 7.2h7.6l-6 4.8 2.4 7.2-6-4.8-6 4.8 2.4-7.2-6-4.8h7.6z"/></svg>'
      },
      {
        key: 'messages',
        text: '我的消息',
        tab: 'messages',
        icon: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5a8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>'
      },
      {
        key: 'treasure',
        text: '我的掉宝',
        tab: 'treasure',
        icon: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>'
      },
      {
        key: 'creator',
        text: '创作中心',
        tab: 'creator',
        icon: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>'
      }
    ];
    // 青少年模式：下拉里的贵族/消息/掉宝/创作中心均受限，只留个人中心
    const minorHiddenQuick = ['noble', 'messages', 'treasure', 'creator'];
    const quickEntries = computed(() => minorMode.value
      ? allQuickEntries.filter(q => minorHiddenQuick.indexOf(q.key) < 0)
      : allQuickEntries);

    const hasPanel = (key) => ['follow', 'history', 'task'].includes(key);

    const handleIconNav = (item) => {
      showDropdown.value = false;
      if (hasPanel(item.key)) {
        if (activeNavPanel.value === item.key) {
          closeNavPanel(0);
        } else {
          openNavPanel(item.key);
        }
        return;
      }
      if (item.route) {
        router.push(item.route);
      }
    };

    const openNavPanel = (key) => {
      if (navPanelTimer) clearTimeout(navPanelTimer);
      activeNavPanel.value = key;
      fetchNavPanelData(key);
    };

    const closeNavPanel = (delay = 250) => {
      if (navPanelTimer) clearTimeout(navPanelTimer);
      navPanelTimer = setTimeout(() => {
        activeNavPanel.value = null;
      }, delay);
    };

    const keepNavPanel = () => {
      if (navPanelTimer) clearTimeout(navPanelTimer);
    };

    const taskProgressPercent = (item) => {
      const target = Number(item.target_count) || 0;
      const done = Number(item.done_count) || 0;
      if (target <= 0) return 0;
      return Math.min((done / target) * 100, 100);
    };

    const taskStatusText = (item) => {
      if (item.is_claimed) return '已领取';
      if (item.is_completed) return '可领取';
      return '进行中';
    };

    const taskStatusClass = (item) => {
      if (item.is_claimed) return 'claimed';
      if (item.is_completed) return 'claimable';
      return 'progress';
    };

    const refreshUserInfo = async () => {
      if (!userStore.userInfo) return;
      try {
        // getUserInfo 返回原始 axios response：{ data: { code, msg, data } }
        const res = await getUserInfo();
        if (res && res.data && res.data.code === 1 && res.data.data) {
          const oldToken = userStore.userInfo?.token;
          const fresh = res.data.data.userinfo || res.data.data;
          userStore.setUserInfo({ ...fresh, token: oldToken });
        }
      } catch (e) { /* 忽略用户信息刷新失败 */ }
    };

    const handleTaskClaim = async (item) => {
      if (item._claiming) return;
      item._claiming = true;
      try {
        let res;
        if (item.task_key === 'daily_sign') {
          res = await dailySignin();
        } else {
          res = await claimTaskReward(item.id);
        }
        if (res && res.code === 1) {
          toast.success(res.msg || '领取成功');
          // 先本地标记，再刷新列表确保与后端同步
          item.is_claimed = true;
          await fetchNavPanelData('task');
          // 刷新后强制更新对应项的领取状态（防止后端旧记录返回 is_claimed=0）
          const updated = taskList.value.find(t => t.id === item.id);
          if (updated) updated.is_claimed = true;
          // 刷新用户信息，更新导航栏积分/经验条
          await refreshUserInfo();
        } else {
          toast.error(res?.msg || '领取失败');
        }
      } catch (e) {
        toast.error(e?.message || '领取失败');
      } finally {
        item._claiming = false;
      }
    };

    const fetchNavPanelData = async (key) => {
      if (!userStore.userInfo) return;
      navPanelLoading.value = true;
      try {
        if (key === 'follow') {
          const res = await getFollowList({ page: 1, limit: 10 });
          if (res.code === 1) followList.value = res.data?.data || res.data?.list || [];
        } else if (key === 'history') {
          const res = await getWatchHistoryList({ page: 1, limit: 10 });
          if (res.code === 1) historyList.value = res.data?.data || res.data?.list || [];
        } else if (key === 'task') {
          const res = await getTaskList();
          if (res.code === 1) {
            const data = res.data || {};
            const daily = data.daily || [];
            const newbie = data.newbie || [];
            taskList.value = [...daily, ...newbie];
          }
        }
      } catch (e) { /* ignore */ }
      finally { navPanelLoading.value = false; }
    };

    const openCategoryPanel = async () => {
      if (categoryPanelTimer) clearTimeout(categoryPanelTimer);
      showCategoryPanel.value = true;
      if (categoryList.value.length === 0) {
        categoryLoading.value = true;
        try {
          const res = await getCategoryList();
          if (res.code === 1) categoryList.value = res.data?.list || res.data || [];
        } catch (e) { /* ignore */ }
        finally { categoryLoading.value = false; }
      }
    };

    const closeCategoryPanel = (delay = 150) => {
      if (categoryPanelTimer) clearTimeout(categoryPanelTimer);
      categoryPanelTimer = setTimeout(() => {
        showCategoryPanel.value = false;
      }, delay);
    };

    const keepCategoryPanel = () => {
      if (categoryPanelTimer) clearTimeout(categoryPanelTimer);
    };

    const goRoomByCategory = (categoryId) => {
      router.push({ path: '/', query: { category: categoryId } });
      showCategoryPanel.value = false;
    };

    const openUserDropdown = () => {
      showDropdown.value = true;
      // 打开用户面板时拉取最新用户信息，更新经验条
      refreshUserInfo();
    };

    const toggleDropdown = () => {
      showDropdown.value = !showDropdown.value;
      if (showDropdown.value) {
        // 打开用户面板时拉取最新用户信息，更新积分/经验条
        refreshUserInfo();
      }
    };

    const handleLogout = () => {
      userStore.clearUserInfo();
      router.push('/login');
      showDropdown.value = false;
    };

    const handleSearch = async () => {
      const keyword = searchKeyword.value.trim();
      if (!keyword) {
        showResults.value = false;
        showSearchResults.value = false;
        return;
      }
      try {
        const response = await apiSearch({ keyword, type: searchType.value });
        if (response.code === 1) {
          const data = response.data || {};
          // 综合搜索：分类结果
          searchAllResults.value = {
            anchor: data.anchor || [],
            room: data.room || [],
            video: data.video || [],
            user: data.user || [],
            post: data.post || []
          };
          // 兼容旧逻辑（视频列表）
          searchResults.value = data.video || data.list || [];
          showSearchResults.value = true;
          showResults.value = false;
        } else {
          searchAllResults.value = { anchor: [], room: [], video: [], user: [], post: [] };
          searchResults.value = [];
          showSearchResults.value = false;
          showResults.value = false;
        }
      } catch (error) {
        console.error('搜索失败:', error);
        // 回退到视频搜索
        try {
          const response = await searchVideo(searchKeyword.value);
          if (response.code === 1) {
            searchResults.value = response.data.list || [];
            showResults.value = searchResults.value.length > 0;
            showSearchResults.value = false;
          } else {
            searchResults.value = [];
            showResults.value = false;
            showSearchResults.value = false;
          }
        } catch (e) {
          searchResults.value = [];
          showResults.value = false;
          showSearchResults.value = false;
        }
      }
    };

    const fetchHotKeywords = async () => {
      try {
        const res = await hotSearch();
        if (res.code === 1) {
          hotKeywords.value = res.data?.list || res.data || [];
        }
      } catch (e) { /* ignore */ }
    };

    const handleSearchFocus = () => {
      searchInputFocused.value = true;
      if (hotKeywords.value.length === 0) {
        fetchHotKeywords();
      }
      if (searchKeyword.value.trim()) {
        // 已有搜索词时显示已有结果
        if (showSearchResults.value || showResults.value) return;
      }
    };

    const handleSearchBlur = () => {
      // 延迟处理以便点击事件能触发
      setTimeout(() => {
        searchInputFocused.value = false;
      }, 200);
    };

    const hasAnyResult = computed(() => {
      const r = searchAllResults.value;
      return (r.anchor && r.anchor.length > 0) ||
             (r.room && r.room.length > 0) ||
             (r.video && r.video.length > 0) ||
             (r.user && r.user.length > 0) ||
             (r.post && r.post.length > 0);
    });

    const goAnchor = (id) => {
      if (!id) return;
      router.push({ name: 'AnchorHome', params: { id } });
      closeSearchPanel();
    };
    const goRoom = (id) => {
      router.push(`/live/${id}`);
      closeSearchPanel();
    };
    const goUser = (id) => {
      if (!id) return;
      router.push({ name: 'AnchorHome', params: { id } });
      closeSearchPanel();
    };
    const goPost = (item) => {
      if (!item) return;
      if (item.user_id) {
        // 跳到作者主页的动态 Tab，可直接查看/点赞/评论
        router.push({ name: 'AnchorHome', params: { id: item.user_id }, query: { tab: 'posts' } });
      } else {
        router.push({ path: '/user', query: { tab: 'posts', post: item.id } });
      }
      closeSearchPanel();
    };

    const closeSearchPanel = () => {
      searchKeyword.value = '';
      showSearchResults.value = false;
      showResults.value = false;
      searchInputFocused.value = false;
    };

    const searchHotKeyword = (kw) => {
      searchKeyword.value = kw;
      handleSearch();
    };

    const formatViews = (views) => {
      if (views >= 10000) {
        return (views / 10000).toFixed(1) + '万';
      }
      return views;
    };

    const handleAvatarError = (e) => {
      e.target.src = defaultAvatarLogo;
    };

    const handleImgError = (e) => {
      e.target.style.display = 'none';
    };

    const getFullImageUrl = (url) => {
      if (!url) return 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" fill="#f0f2f5"/><text x="32" y="40" font-size="32" text-anchor="middle" fill="#bfbfbf">U</text></svg>');
      if (url.startsWith('http') || url.startsWith('data:')) return url;
      return `${BASE_URL}${url}`;
    };

    const goToVideo = (id) => {
      router.push(`/live/${id}`);
      searchKeyword.value = '';
      showResults.value = false;
    };

    const handleClickOutside = (event) => {
      if (dropdownTrigger.value && !dropdownTrigger.value.contains(event.target)) {
        showDropdown.value = false;
      }
      const searchBox = document.querySelector('.search-box');
      if (searchBox && !searchBox.contains(event.target)) {
        showResults.value = false;
        showSearchResults.value = false;
        searchInputFocused.value = false;
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
      refreshUserInfo();
      fetchMinorMode();
      window.addEventListener('minor-mode-changed', onMinorModeChanged);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('minor-mode-changed', onMinorModeChanged);
    });

    return {
      userStore,
      showDropdown,
      toggleDropdown,
      openUserDropdown,
      dropdownTrigger,
      handleLogout,
      searchKeyword,
      searchResults,
      showResults,
      handleSearch,
      formatViews,
      getFullImageUrl,
      goToVideo,
      handleAvatarError,
      handleImgError,
      iconNavItems,
      handleIconNav,
      hasPanel,
      activeNavPanel,
      navPanelLoading,
      followList,
      historyList,
      taskList,
      openNavPanel,
      closeNavPanel,
      keepNavPanel,
      taskProgressPercent,
      taskStatusText,
      taskStatusClass,
      handleTaskClaim,
      showCategoryPanel,
      categoryLoading,
      categoryList,
      openCategoryPanel,
      closeCategoryPanel,
      keepCategoryPanel,
      goRoomByCategory,
      quickEntries,
      liveLevel,
      liveExp,
      liveNeedExp,
      levelPercent,
      genderIcon,
      // 综合搜索
      searchType,
      searchAllResults,
      showSearchResults,
      hotKeywords,
      searchInputFocused,
      fetchHotKeywords,
      handleSearchFocus,
      handleSearchBlur,
      hasAnyResult,
      goAnchor,
      goRoom,
      goUser,
      goPost,
      closeSearchPanel,
      searchHotKeyword
    };
  }
};
</script>

<style scoped>
.header {
  background: linear-gradient(90deg, #3b2cff 0%, #6a11cb 50%, #2575fc 100%);
  color: #fff;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.2);
}

.header-inner {
  display: flex;
  align-items: center;
  height: 56px;
  gap: 24px;
  min-width: 0;
}

.container {
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 15px;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
}

.logo {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 100%;
}

.logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.logo-image {
  height: 36px;
  width: auto;
  max-width: 160px;
  object-fit: contain;
  transition: opacity 0.3s ease;
}

.logo-link:hover .logo-image {
  opacity: 0.85;
}

/* 主导航 */
.main-nav {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.main-nav-item {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 14px;
  border-radius: 16px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.main-nav-item:hover,
.main-nav-item.active {
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
}

.category-nav {
  position: relative;
}

.category-panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: 320px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  z-index: 1001;
  overflow: hidden;
  color: #333;
  padding: 12px;
}

.category-panel-arrow {
  position: absolute;
  top: -6px;
  left: 50%;
  margin-left: -6px;
  width: 12px;
  height: 12px;
  background: #fff;
  transform: rotate(45deg);
  box-shadow: -2px -2px 6px rgba(0, 0, 0, 0.06);
}

.category-panel-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.category-panel-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 4px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.category-panel-item:hover {
  background: #f5f5f5;
}

.category-panel-item img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  background: #eee;
}

.category-panel-item span {
  font-size: 12px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.category-panel-loading,
.category-panel-empty {
  padding: 30px 0;
  text-align: center;
  color: #999;
  font-size: 13px;
}

.category-panel-fade-enter-active {
  transition: all 0.2s ease-out;
}

.category-panel-fade-leave-active {
  transition: all 0.15s ease-in;
}

.category-panel-fade-enter-from,
.category-panel-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-8px) scale(0.96);
}

/* 图标导航 */
.icon-nav {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-left: auto;
  position: relative;
}

.icon-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 60px;
  padding: 4px 6px;
  border-radius: 8px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.85);
  transition: all 0.2s ease;
}

.icon-nav-item:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
  transform: translateY(-1px);
}

.icon-nav-svg {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 22px;
}

.icon-nav-svg svg {
  width: 22px;
  height: 22px;
}

.icon-nav-text {
  font-size: 12px;
  line-height: 1.2;
}

.icon-nav-item.has-panel { cursor: default; }

.icon-nav-item.active,
.icon-nav-item:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
}

/* 顶部导航下拉面板 */
.nav-panel {
  position: absolute;
  top: 100%;
  right: 0;
  width: 280px;
  padding-top: 8px;
  background: transparent;
  z-index: 1001;
  transform-origin: top right;
  pointer-events: none;
}

.nav-panel > * {
  pointer-events: auto;
}

.nav-panel-arrow {
  position: absolute;
  top: 2px;
  right: 118px;
  width: 12px;
  height: 12px;
  background: #fff;
  transform: rotate(45deg);
  box-shadow: -2px -2px 6px rgba(0, 0, 0, 0.06);
}

.nav-panel-body {
  max-height: 360px;
  overflow-y: auto;
  position: relative;
  padding: 8px 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  color: #333;
}

.nav-panel-list {
  display: flex;
  flex-direction: column;
}

.nav-panel-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  cursor: pointer;
  transition: background 0.2s;
}

.nav-panel-item:hover { background: #f8f8f8; }

.nav-panel-item img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  background: #eee;
  flex-shrink: 0;
}

.nav-panel-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.nav-panel-name {
  font-size: 13px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav-panel-meta {
  font-size: 11px;
  color: #999;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav-panel-tag {
  font-size: 10px;
  color: #fff;
  background: #ff4d6d;
  padding: 1px 5px;
  border-radius: 4px;
  width: fit-content;
}

.nav-panel-reward {
  font-size: 12px;
  color: #ff9600;
  font-weight: 700;
  flex-shrink: 0;
}

.nav-panel-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px;
  color: #999;
  font-size: 13px;
  text-align: center;
}

.nav-panel-empty img {
  width: 80px;
  height: 80px;
  object-fit: contain;
  margin-bottom: 8px;
}

.nav-panel-empty p {
  font-size: 14px;
  color: #666;
  margin: 0 0 4px;
}

.nav-panel-empty span {
  font-size: 12px;
  color: #999;
}

.nav-panel-loading {
  padding: 30px 0;
  text-align: center;
  color: #999;
  font-size: 13px;
}

.nav-panel-more {
  display: block;
  text-align: center;
  padding: 10px;
  font-size: 12px;
  color: #666;
  text-decoration: none;
  border-top: 1px solid #f0f0f0;
  margin-top: 4px;
}

.nav-panel-more:hover { color: #ff6a00; }

/* 任务面板特有样式 */
.task-panel-item {
  flex-wrap: wrap;
  align-items: flex-start;
  padding: 10px 14px;
}

.task-panel-item .nav-panel-info {
  width: 100%;
  gap: 4px;
}

.task-type-tag {
  display: inline-block;
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 4px;
  margin-left: 6px;
  vertical-align: middle;
  font-weight: 500;
  line-height: 1.4;
}

.task-type-tag.daily {
  background: #fff0e6;
  color: #ff6a00;
}

.task-type-tag.newbie {
  background: #e6f7ff;
  color: #1890ff;
}

.task-progress-bar {
  width: 100%;
  height: 5px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 4px;
}

.task-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.task-progress-text {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
}

.task-status-tag {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 8px;
  flex-shrink: 0;
  font-weight: 500;
  margin-left: 6px;
  align-self: center;
  border: none;
  cursor: default;
}

.task-status-tag.progress {
  background: #f0f0f0;
  color: #999;
}

.task-status-tag.claimable {
  background: #fff0e6;
  color: #ff6a00;
  cursor: pointer;
  transition: all 0.2s;
}

.task-status-tag.claimable:hover {
  background: #ff6a00;
  color: #fff;
}

.task-status-tag.claimed {
  background: #f5f5f5;
  color: #bbb;
  cursor: default;
}

.nav-panel-item .nav-panel-reward {
  align-self: center;
}

.nav-panel-fade-enter-active {
  transition: all 0.2s ease-out;
}

.nav-panel-fade-leave-active {
  transition: all 0.15s ease-in;
}

.nav-panel-fade-enter-from,
.nav-panel-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.96);
}

.search-box {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 20px;
  padding: 4px 4px 4px 14px;
  width: 320px;
  position: relative;
  transition: all 0.3s ease;
  margin-left: 24px;
}

.search-box:focus-within {
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.25);
}

.search-box input {
  background: transparent;
  border: none;
  color: #333;
  width: 100%;
  outline: none;
  padding: 5px 0;
  font-size: 13px;
}

.search-box input::placeholder {
  color: #999;
}

/* 搜索类型选择器 */
.search-type {
  background: transparent;
  border: none;
  color: #666;
  font-size: 12px;
  outline: none;
  cursor: pointer;
  padding: 4px 6px 4px 2px;
  border-right: 1px solid #e5e5e5;
  margin-right: 6px;
  font-family: inherit;
}
.search-type option {
  font-size: 13px;
  color: #333;
}

.search-btn {
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.search-btn:hover {
  filter: brightness(1.1);
}

.user-area {
  display: flex;
  align-items: center;
  position: relative;
  flex-shrink: 0;
}

.login-btn, .register-btn {
  padding: 6px 16px;
  border-radius: 16px;
  margin-left: 10px;
  cursor: pointer;
  font-weight: bold;
  text-decoration: none;
  display: inline-block;
  transition: all 0.3s ease;
  font-size: 13px;
}

.login-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.8);
  color: #fff;
}

.login-btn:hover {
  background: rgba(255, 255, 255, 0.12);
}

.register-btn {
  background: linear-gradient(45deg, #ff9600, #ffaa33);
  border: 1px solid transparent;
  color: #fff;
  box-shadow: 0 2px 8px rgba(255, 150, 0, 0.3);
}

.register-btn:hover {
  filter: brightness(1.1);
}

.avatar-wrap {
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: background 0.2s;
}

.avatar-wrap:hover {
  background: rgba(255, 255, 255, 0.15);
}

.avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.5);
}

/* 用户卡片下拉 */
.user-card-dropdown {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 320px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  z-index: 1000;
  overflow: hidden;
  color: #333;
  transform-origin: top right;
}

.ucd-arrow {
  position: absolute;
  top: -6px;
  right: 18px;
  width: 12px;
  height: 12px;
  background: #fff;
  transform: rotate(45deg);
  box-shadow: -2px -2px 6px rgba(0, 0, 0, 0.06);
}

.ucd-header {
  display: flex;
  justify-content: flex-end;
  padding: 12px 16px 0;
}

.ucd-logout {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #666;
  text-decoration: none;
  transition: color 0.2s;
}

.ucd-logout:hover {
  color: #ff6a00;
}

.ucd-user {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 20px 16px;
}

.ucd-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #ffe4cc;
  margin-bottom: 10px;
}

.ucd-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.ucd-nickname {
  font-size: 16px;
  font-weight: 700;
  color: #333;
}

.ucd-gender {
  display: flex;
  align-items: center;
}

.ucd-edit {
  display: flex;
  align-items: center;
  color: #ff9600;
  transition: transform 0.2s;
}

.ucd-edit:hover {
  transform: scale(1.1);
}

.ucd-level {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.ucd-level-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: linear-gradient(90deg, #10b981, #34d399);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
}

.ucd-level-badge svg {
  width: 10px;
  height: 10px;
}

.ucd-level-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  color: #ff9600;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
}

.ucd-level-tag:hover {
  color: #ff6a00;
  transform: scale(1.05);
}

.ucd-progress-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 220px;
}

.ucd-progress-bar {
  flex: 1;
  height: 8px;
  background: #eee;
  border-radius: 4px;
  overflow: hidden;
}

.ucd-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  border-radius: 4px;
}

.ucd-level-label {
  font-size: 11px;
  color: #999;
}

.ucd-progress-text {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.ucd-assets {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
}

.ucd-assets-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ucd-assets-label {
  font-size: 13px;
  color: #666;
}

.ucd-assets-item {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 13px;
  color: #333;
  font-weight: 600;
}

.ucd-recharge {
  padding: 5px 18px;
  border-radius: 14px;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  transition: filter 0.2s;
}

.ucd-recharge:hover {
  filter: brightness(1.1);
}

.ucd-quick {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  padding: 14px 10px;
  gap: 4px;
}

.ucd-quick-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  text-decoration: none;
  color: #666;
  border-radius: 8px;
  padding: 6px 0;
  transition: all 0.2s;
}

.ucd-quick-item:hover {
  background: #fff8f0;
  color: #ff6a00;
}

.ucd-quick-icon {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff5eb;
  color: #ff9600;
}

.ucd-quick-icon svg {
  width: 20px;
  height: 20px;
}

.ucd-quick-text {
  font-size: 12px;
}

/* 搜索结果 */
.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 8px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 1200;
}

.search-result-item {
  display: flex;
  padding: 10px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f5f5f5;
}

.search-result-item:last-child {
  border-bottom: none;
}

.search-result-item:hover {
  background-color: #f8f8f8;
}

.result-thumb {
  width: 100px;
  height: 56px;
  object-fit: cover;
  border-radius: 4px;
  margin-right: 10px;
  background: #eee;
}

.result-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.result-title {
  font-size: 13px;
  color: #333;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11px;
  color: #999;
}

.result-author {
  color: #666;
}

/* 综合搜索结果浮层 */
.search-all-results {
  max-height: 480px;
  padding: 4px 0;
}
.result-group {
  border-bottom: 1px solid #f5f5f5;
}
.result-group:last-child {
  border-bottom: none;
}
.result-group-title {
  padding: 8px 12px 4px;
  font-size: 12px;
  font-weight: 700;
  color: #ff6a00;
  background: #fff8f0;
  letter-spacing: 0.5px;
}
.no-result-tip {
  padding: 30px 0;
  text-align: center;
  color: #999;
  font-size: 13px;
}
.anchor-result-item .result-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 10px;
  background: #eee;
  flex-shrink: 0;
}
.post-result-info {
  flex: 1;
}
.post-result-title {
  -webkit-line-clamp: 1;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

/* 热门搜索词浮层 */
.hot-keywords-panel {
  padding: 12px;
}
.hot-title {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 700;
  color: #ff6a00;
  margin-bottom: 10px;
}
.hot-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.hot-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 13px;
  color: #333;
}
.hot-item:hover {
  background: #fff5eb;
  color: #ff6a00;
}
.hot-rank {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: #eee;
  color: #999;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}
.hot-rank.rank-1 { background: #ff4d4f; color: #fff; }
.hot-rank.rank-2 { background: #ff7a45; color: #fff; }
.hot-rank.rank-3 { background: #ffa940; color: #fff; }
.hot-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 动画 */
.dropdown-fade-enter-active {
  transition: all 0.25s ease-out;
}

.dropdown-fade-leave-active {
  transition: all 0.2s ease-in;
}

.dropdown-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.96);
}

.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.96);
}

@media (max-width: 900px) {
  .icon-nav {
    display: none;
  }
  .search-box {
    flex: 1 1 180px;
    width: 180px;
    min-width: 0;
    margin-left: 0;
  }
}

@media (max-width: 640px) {
  .header-inner {
    height: 52px;
    gap: 10px;
  }

  .container {
    padding: 0 10px;
  }

  .logo-image {
    height: 32px;
    max-width: 64px;
  }

  .main-nav {
    display: none;
  }

  .search-box {
    flex: 1 1 auto;
    width: auto;
    margin-left: 0;
    padding-left: 10px;
  }

  .search-type {
    display: none;
  }

  .search-box input {
    min-width: 0;
    font-size: 12px;
  }

  .search-btn {
    padding: 6px 8px;
  }

  .login-btn,
  .register-btn {
    padding: 5px 9px;
    margin-left: 4px;
    font-size: 12px;
  }
}
</style>
